import { useRef } from 'react';
import { useGSAP } from '@gsap/react';
import {
  ComponentRendering,
  Field,
  ImageFieldValue,
  LinkField,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { sanitize } from 'isomorphic-dompurify';

import { Button, ButtonVariantType } from '@gn-audio/jss-components/components';

import { getSharedArray, getSharedProperty, mapSharedPropertiesToFields } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { withTheme } from 'common/jabraTheme/withTheme';
import { ComponentProps } from 'lib/component-props';

type ScrollingKSPSpot = {
  id: string;
  fields: {
    id: Field<string>;
    title: Field<string>;
    titleShared: Field<string>;
    backgroundColor: Field<string>;
    backgroundColorShared: Field<string>;
    description: Field<string>;
    descriptionShared: Field<string>;
    image: Field<ImageFieldValue>;
    imageShared: Field<ImageFieldValue>;
    firstCtaButtonText: Field<string>;
    firstCtaButtonTextShared: Field<string>;
    firstCtaButtonLink: LinkField;
    firstCtaButtonLinkShared: LinkField;
    firstCtaButtonVariant: Field<string>;
    firstCtaButtonVariantShared: Field<string>;
    secondaCtaButtonText: Field<string>;
    secondCtaButtonTextShared: Field<string>;
    secondCtaButtonLink: LinkField;
    secondCtaButtonLinkShared: LinkField;
    secondCtaButtonVariant: Field<string>;
    secondButtonVariantShared: Field<string>;
  };
};
type ScrollingKSPSpotItem = {
  title: Field<string>;
  backgroundColor: Field<string>;
  description: Field<string>;
  image: Field<ImageFieldValue>;
  firstCtaButtonText: Field<string>;
  firstCtaButtonLink: LinkField;
  firstCtaButtonVariant: Field<string>;
  secondCtaButtonText: Field<string>;
  secondCtaButtonLink: LinkField;
  secondCtaButtonVariant: Field<string>;
  id: Field<string>;
};
type ScrollingKSpSpotProps = {
  fields: ScrollingKSPSpotRendering;
} & ComponentProps;
type ScrollingKSPSpotRendering = {
  spots: ScrollingKSPSpot[];
  spotsShared: ScrollingKSPSpot[];
  fontColor: Field<string>;
  fontColorShared: Field<string>;
  tag: Field<string>;
  tagShared: Field<string>;
} & ComponentRendering;

gsap.registerPlugin(ScrollTrigger);

const ScrollingKSPSpot = ({ fields }: ScrollingKSpSpotProps) => {
  const mappedSpots = getSharedArray(fields, 'spots');
  const tagLine = getSharedDictionary(fields, 'tag')?.value;
  const fontColor = getSharedProperty(fields, 'fontColor')?.value;

  const fontColorStyle = {
    color: fontColor ? fontColor : 'var(--fgColor)',
    backgroundColor: 'var(--bgColor)',
  };

  const textRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  const spots = mappedSpots.map((spot) => {
    return { id: spot.id, fields: mapSharedPropertiesToFields<ScrollingKSPSpotItem>(spot.fields) };
  });

  useGSAP(() => {
    const textContainer = textRef.current!;
    const imageContainer = imageRef.current!;
    const children = Array.from(textContainer.children);
    const mm = gsap.matchMedia();

    mm.add('(min-width: 768px)', () => {
      let wasScrollTriggerRefreshed = false;

      children.forEach((block, index) => {
        gsap.timeline({
          scrollTrigger: {
            trigger: block,
            start: 'top center',
            end: 'bottom center',
            onToggle: (self) => {
              gsap.to(block, { opacity: self.isActive ? 1 : 0.3, duration: 0.6 });
              gsap.to(imageContainer.children[index], {
                opacity: self.isActive ? 1 : 0,
                duration: 0.6,
              });
            },
          },
        });
      });

      gsap.timeline({
        scrollTrigger: {
          trigger: textContainer,
          pin: imageContainer,
          start: 'top top',
          end: 'bottom 90%',
          pinSpacing: false,
          onEnter: () => {
            if (!wasScrollTriggerRefreshed) {
              ScrollTrigger.refresh();
              wasScrollTriggerRefreshed = true;
            }
          },
        },
      });
    });
  }, []);

  return (
    <div className="bg-theme min-h-screen">
      <div className="container flex flex-col md:flex-row">
        <div
          style={{
            borderColor: 'var(--fgColor)',
          }}
          className="text-container flex-1 border-l-2 pl-12"
        >
          <div
            className="w-fit rounded-full px-6 py-1"
            style={{
              color: 'var(--bgColor)',
              backgroundColor: 'var(--fgColor)',
            }}
          >
            {tagLine}
          </div>
          <div ref={textRef}>
            {spots.map(({ fields, id }) => (
              <div
                className="duration-600 w-full flex-col justify-center py-12 opacity-100 transition-opacity ease-linear md:w-[420px] md:py-52 md:opacity-30"
                key={id}
              >
                <h2
                  className="heading-1 font-bold"
                  style={fontColorStyle}
                  dangerouslySetInnerHTML={{ __html: sanitize(fields.title.value as string) }}
                />

                <p
                  className="paragraph"
                  style={fontColorStyle}
                  dangerouslySetInnerHTML={{ __html: sanitize(fields.description.value as string) }}
                />

                <div className="flex flex-wrap gap-6 pt-5">
                  {fields.firstCtaButtonText?.value && fields.firstCtaButtonLink?.value && (
                    <Button
                      asLink
                      href={fields.firstCtaButtonLink.value.href}
                      variant={fields.firstCtaButtonVariant.value as ButtonVariantType}
                      size="m"
                    >
                      {fields.firstCtaButtonText.value}
                    </Button>
                  )}
                  {fields.secondCtaButtonText?.value && fields.secondCtaButtonLink?.value && (
                    <Button
                      asLink
                      href={fields.secondCtaButtonLink.value.href}
                      variant={fields.secondCtaButtonVariant.value as ButtonVariantType}
                      size="m"
                    >
                      {fields.secondCtaButtonText.value}
                    </Button>
                  )}
                </div>
                <img
                  src={fields.image.value.src}
                  alt={fields.image.value.alt as string}
                  className="mt-6 block w-full md:hidden"
                />
              </div>
            ))}
          </div>
        </div>
        <div
          className="image-container relative hidden h-screen w-full flex-1 items-center justify-center md:flex"
          ref={imageRef}
        >
          {spots.map(({ fields, id }) => (
            <img
              key={id}
              src={fields.image.value.src}
              alt={fields.image.value.alt as string}
              className="absolute max-h-[60vh] opacity-0"
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default withTheme()(withDatasourceCheck()(ScrollingKSPSpot));
